import { zodSchemaFor } from "@app/dpp-schemas";
import type { PassportSchemaDef } from "@app/dpp-schemas";

/**
 * Turns an IDPP data-collection workbook (one product form per sheet, built by
 * the template generator) into the CSV the existing products import endpoint
 * takes — so upsert-by-SKU, draft validation, plan caps and audit all stay in
 * one server path. Parsing happens here, with no dependency: an .xlsx is a ZIP
 * of XML parts, and the browser already ships deflate (DecompressionStream).
 *
 * Form layout contract (see the generator): cell G2 holds "__schema__:<id>" on
 * product sheets ("__example__:<id>" on the read-only example, never imported);
 * from row 3 on, column G/H holds the field id of the input cell in B/E.
 *
 * Group (list) fields sit on their own table sheets, shared by all products:
 * hidden row 3 holds "product.sku" in A, the item keys after it and a
 * "__group__:<schemaId>:<fieldId>" marker; data starts at row 5 and each row
 * joins the product sheet with the same SKU. Rows marked "__example__" in the
 * marker column are the template's sample and are skipped.
 */

export interface ExcelImport {
  csv: string;
  /** Sheet name per CSV data line: rowSheets[0] is line 2 (line 1 is the header). */
  rowSheets: string[];
}

/** One row of a group (list) field: import-ready strings keyed by item key. */
export type GroupRow = Record<string, string>;

/** One filled product sheet. Values are import-ready strings ("true"/"false", ISO dates) keyed by field id. */
export interface ExcelForm {
  sheet: string;
  schemaId: string;
  values: Record<string, string>;
  /** List rows from the group sheets whose SKU matched this product, keyed by group field id. */
  groups: Record<string, GroupRow[]>;
}

/** A list-sheet row whose SKU matches no product sheet — reported, never imported. */
export interface OrphanRow {
  sheet: string;
  row: number;
  sku: string;
}

type CellValue = string | number | boolean;
type Cells = Map<string, CellValue>;

const SCHEMA_MARKER = "__schema__:";
const GROUP_MARKER = "__group__:";
const EXAMPLE_ROW = "__example__";
const GROUP_FIRST_ROW = 5;
/** Pseudo field ids the template uses for the product-level columns. */
export const SKU_ID = "product.sku";
export const NAME_ID = "product.name";
const PAIRS = [
  ["B", "G"],
  ["E", "H"],
] as const;
const YES = new Set(["evet", "yes", "true"]);
const NO = new Set(["hayır", "hayir", "no", "false"]);

export async function excelToImportCsv(
  data: ArrayBuffer,
  getDef: (schemaId: string) => PassportSchemaDef | undefined,
): Promise<ExcelImport> {
  const { forms } = await readExcelForms(data, getDef);
  return { csv: formsToCsv(forms), rowSheets: forms.map((f) => f.sheet) };
}

export async function readExcelForms(
  data: ArrayBuffer,
  getDef: (schemaId: string) => PassportSchemaDef | undefined,
): Promise<{ forms: ExcelForm[]; orphans: OrphanRow[] }> {
  const sheets = await readWorkbook(new Uint8Array(data));
  const forms: ExcelForm[] = [];
  const groupSheets: { name: string; cells: Cells }[] = [];
  for (const { name, cells } of sheets) {
    if (groupMarker(cells)) {
      groupSheets.push({ name, cells });
      continue;
    }
    const marker = cells.get("G2");
    if (typeof marker !== "string" || !marker.startsWith(SCHEMA_MARKER)) continue;
    const schemaId = marker.slice(SCHEMA_MARKER.length);
    const def = getDef(schemaId);
    const typeOf = new Map<string, string>();
    for (const category of def?.categories ?? []) {
      for (const field of category.fields) typeOf.set(field.id, field.type);
    }
    const values: Record<string, string> = {};
    for (let row = 3; row <= lastRow(cells); row++) {
      for (const [input, idCol] of PAIRS) {
        const id = cells.get(`${idCol}${row}`);
        const raw = cells.get(`${input}${row}`);
        if (typeof id !== "string" || raw === undefined || raw === "") continue;
        values[id.trim()] = toCsvValue(raw, typeOf.get(id.trim()));
      }
    }
    // A copied-but-untouched form is not an error, just nothing to import.
    if (Object.values(values).every((v) => v.trim() === "")) continue;
    forms.push({ sheet: name, schemaId, values, groups: {} });
  }

  const orphans: OrphanRow[] = [];
  for (const { name, cells } of groupSheets) {
    const { col: markerCol, value } = groupMarker(cells)!;
    const [schemaId = "", fieldId = ""] = value.slice(GROUP_MARKER.length).split(":");
    const field = getDef(schemaId)
      ?.categories.flatMap((c) => c.fields)
      .find((f) => f.id === fieldId);
    const keys = new Map<string, string>(); // column → item key
    for (const [ref, v] of cells) {
      const col = ref.replace(/\d+$/, "");
      if (ref === `${col}3` && col !== "A" && col !== markerCol && typeof v === "string")
        keys.set(col, v.trim());
    }
    for (let row = GROUP_FIRST_ROW; row <= lastRow(cells); row++) {
      if (cells.get(`${markerCol}${row}`) === EXAMPLE_ROW) continue;
      const item: GroupRow = {};
      for (const [col, key] of keys) {
        const raw = cells.get(`${col}${row}`);
        if (raw === undefined || raw === "") continue;
        const v = toCsvValue(raw, field?.itemFields?.find((i) => i.key === key)?.type);
        if (v !== "") item[key] = v;
      }
      const sku = String(cells.get(`A${row}`) ?? "").trim();
      if (!sku && Object.keys(item).length === 0) continue;
      const form = forms.find((f) => f.schemaId === schemaId && (f.values[SKU_ID] ?? "").trim() === sku);
      if (!form || !sku) {
        orphans.push({ sheet: name, row, sku });
        continue;
      }
      (form.groups[fieldId] ??= []).push(item);
    }
  }
  return { forms, orphans };
}

function groupMarker(cells: Cells): { col: string; value: string } | undefined {
  for (const [ref, v] of cells) {
    if (typeof v === "string" && v.startsWith(GROUP_MARKER) && /^[A-Z]+3$/.test(ref)) {
      return { col: ref.slice(0, -1), value: v };
    }
  }
  return undefined;
}

function lastRow(cells: Cells): number {
  return Math.max(...[...cells.keys()].map((ref) => Number(ref.replace(/^[A-Z]+/, ""))));
}

/**
 * CSV for POST /products/import: one line per form, in order (line n+2 is forms[n]).
 * A group column carries its rows as JSON; the server coerces item types.
 */
export function formsToCsv(forms: readonly ExcelForm[]): string {
  const attrIds = [...new Set(forms.flatMap((f) => Object.keys(f.values)))].filter(
    (id) => id !== SKU_ID && id !== NAME_ID,
  );
  const groupIds = [...new Set(forms.flatMap((f) => Object.keys(f.groups)))];
  const lines = [["sku", "name", "schemaId", ...attrIds, ...groupIds]];
  for (const f of forms) {
    lines.push([
      f.values[SKU_ID] ?? "",
      f.values[NAME_ID] ?? "",
      f.schemaId,
      ...attrIds.map((id) => f.values[id] ?? ""),
      ...groupIds.map((id) => {
        const rows = (f.groups[id] ?? []).filter((r) => !isBlankRow(r));
        return rows.length > 0 ? JSON.stringify(rows) : "";
      }),
    ]);
  }
  return lines.map((cols) => cols.map(csvCell).join(",")).join("\n");
}

export type FieldIssue = "required" | "invalid";

/** Typed attr bag from import-ready strings — the shape the API stores after CSV coercion. */
export function toAttrs(
  def: PassportSchemaDef,
  values: Record<string, string>,
  groups: Record<string, GroupRow[]> = {},
): Record<string, unknown> {
  const attrs: Record<string, unknown> = {};
  for (const category of def.categories) {
    for (const field of category.fields) {
      if (field.type === "group") {
        // Blank cells are "not provided"; an all-blank row is dropped, an empty list stays absent.
        const rows = (groups[field.id] ?? [])
          .filter((r) => !isBlankRow(r))
          .map((r) => {
            const row: Record<string, unknown> = {};
            for (const item of field.itemFields ?? []) {
              const v = (r[item.key] ?? "").trim();
              if (v !== "") row[item.key] = typed(item.type, v);
            }
            return row;
          });
        if (rows.length > 0) attrs[field.id] = rows;
        continue;
      }
      const v = (values[field.id] ?? "").trim();
      if (v !== "") attrs[field.id] = typed(field.type, v);
    }
  }
  return attrs;
}

export function isBlankRow(row: GroupRow): boolean {
  return Object.values(row).every((v) => v.trim() === "");
}

function typed(type: string, v: string): unknown {
  if (type === "number" || type === "percentage") return Number.isNaN(Number(v)) ? v : Number(v);
  if (type === "boolean") return v === "true" ? true : v === "false" ? false : v;
  return v;
}

/** Issue-map key of one list cell, next to plain field ids. */
export function groupCellKey(fieldId: string, row: number, key: string): string {
  return `${fieldId}[${row}].${key}`;
}

/**
 * Per-field problems against the PUBLISH rules, so an imported product is
 * complete, not just a valid draft. SKU/name limits mirror createProductSchema.
 */
export function formIssues(
  def: PassportSchemaDef,
  values: Record<string, string>,
  groups: Record<string, GroupRow[]> = {},
): Map<string, FieldIssue> {
  const issues = new Map<string, FieldIssue>();
  for (const [id, max] of [
    [SKU_ID, 64],
    [NAME_ID, 200],
  ] as const) {
    const v = (values[id] ?? "").trim();
    if (v === "") issues.set(id, "required");
    else if (v.length > max) issues.set(id, "invalid");
  }
  const attrs = toAttrs(def, values, groups);
  const parsed = zodSchemaFor(def, { mode: "publish" }).safeParse(attrs);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const [id, row, key] = issue.path.map(String);
      if (!id) continue;
      if (Array.isArray(attrs[id])) {
        // A list cell: flag the cell itself (by its row in `groups`, blank rows were dropped
        // from attrs) and the list, so the product counts as not ready.
        const source = (groups[id] ?? []).flatMap((r, i) => (isBlankRow(r) ? [] : [i]));
        const at = source[Number(row)];
        if (at !== undefined && key !== undefined) {
          const blank = (groups[id]?.[at]?.[key] ?? "").trim() === "";
          issues.set(groupCellKey(id, at, key), blank ? "required" : "invalid");
        }
        issues.set(id, "invalid");
      } else {
        issues.set(id, (values[id] ?? "").trim() === "" ? "required" : "invalid");
      }
    }
  }
  return issues;
}

function toCsvValue(raw: CellValue, type: string | undefined): string {
  if (type === "boolean") {
    const lower = String(raw).trim().toLocaleLowerCase("tr");
    if (raw === true || YES.has(lower)) return "true";
    if (raw === false || NO.has(lower)) return "false";
  }
  // Excel stores dates as serial day counts from 1899-12-30 (the 1900 leap-year bug baked in).
  if (type === "date" && typeof raw === "number") {
    return new Date(Date.UTC(1899, 11, 30) + Math.round(raw) * 86_400_000).toISOString().slice(0, 10);
  }
  return String(raw).trim();
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/* --- Minimal .xlsx reader ------------------------------------------------------ */

async function readWorkbook(bytes: Uint8Array<ArrayBuffer>): Promise<{ name: string; cells: Cells }[]> {
  const files = await unzip(bytes);
  const text = (path: string): string => {
    const f = files.get(path);
    return f ? new TextDecoder().decode(f) : "";
  };
  const shared = [...text("xl/sharedStrings.xml").matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) =>
    joinText(m[1] ?? ""),
  );
  const targets = new Map<string, string>();
  for (const m of text("xl/_rels/workbook.xml.rels").matchAll(/<Relationship\b[^>]*>/g)) {
    const id = attr(m[0], "Id");
    const target = attr(m[0], "Target");
    if (id && target) targets.set(id, target.startsWith("/") ? target.slice(1) : `xl/${target}`);
  }
  const sheets: { name: string; cells: Cells }[] = [];
  for (const m of text("xl/workbook.xml").matchAll(/<sheet\b[^>]*>/g)) {
    const path = targets.get(attr(m[0], "r:id") ?? "");
    if (path)
      sheets.push({ name: decodeXml(attr(m[0], "name") ?? ""), cells: readCells(text(path), shared) });
  }
  return sheets;
}

function readCells(xml: string, shared: string[]): Cells {
  const cells: Cells = new Map();
  for (const m of xml.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
    const ref = attr(m[1] ?? "", "r");
    const body = m[2] ?? "";
    if (!ref) continue;
    const type = attr(m[1] ?? "", "t");
    const v = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
    let value: CellValue | undefined;
    if (type === "inlineStr") value = joinText(body);
    else if (v === undefined) continue;
    else if (type === "s") value = shared[Number(v)] ?? "";
    else if (type === "b") value = v === "1";
    else if (type === "str" || type === "e") value = decodeXml(v);
    else value = Number(v);
    cells.set(ref, value);
  }
  return cells;
}

/** Concatenates the <t> runs of a string item (rich text splits one value into runs). */
function joinText(xml: string): string {
  return [...xml.replace(/<rPh\b[\s\S]*?<\/rPh>/g, "").matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)]
    .map((m) => decodeXml(m[1] ?? ""))
    .join("");
}

function attr(tag: string, name: string): string | undefined {
  return new RegExp(`\\s${name}="([^"]*)"`).exec(tag)?.[1];
}

function decodeXml(s: string): string {
  return s.replace(/&(?:#x([0-9a-f]+)|#(\d+)|(lt|gt|amp|quot|apos));/gi, (_, hex, dec, named) => {
    if (hex) return String.fromCodePoint(parseInt(hex, 16));
    if (dec) return String.fromCodePoint(Number(dec));
    return { lt: "<", gt: ">", amp: "&", quot: '"', apos: "'" }[named as string] ?? "";
  });
}

/** Reads a ZIP through its central directory (sizes there are reliable even when local headers defer them). */
async function unzip(bytes: Uint8Array<ArrayBuffer>): Promise<Map<string, Uint8Array>> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65_557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("Not an .xlsx file");
  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const files = new Map<string, Uint8Array>();
  for (let n = 0; n < count; n++) {
    if (view.getUint32(p, true) !== 0x02014b50) throw new Error("Corrupt .xlsx file");
    const method = view.getUint16(p + 10, true);
    const size = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const local = view.getUint32(p + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    // Only the XML parts are needed; skip images and other binaries.
    if (!name.endsWith(".xml") && !name.endsWith(".rels")) continue;
    const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    const raw = bytes.subarray(start, start + size);
    if (method === 0) files.set(name, raw);
    else if (method === 8) files.set(name, await inflate(raw));
    else throw new Error(`Unsupported .xlsx compression (${method})`);
  }
  return files;
}

async function inflate(raw: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}
